import { Box, useTheme } from "@mui/material";
import { Question } from "../../models/types";
import { NavbarIcon, INavbarIconVariant } from "../../atoms/navbar/Icon";
import { TableTextWithEllipsis } from "../TableTextWithEllipsis";
import { QuestionnaireBuilderQuestionTitle } from "./QuestionnaireBuilderQuestionTitle";
import { ListArrangement } from "../../organisms/list-arrangement/ListArrangement";
import { memo, useCallback } from "react";

type QuestionnaireBuilderQuestionGroupProps = {
  question: Question;
  selected: boolean;
  selectedQuestion: Question | null;
  onClick: (question: Question) => void;
  onAdd?: () => void;
  index: number;
  updateLayout?: (updatedLayout: any[]) => void;
};

export const QuestionnaireBuilderQuestionGroup = memo(
  (props: QuestionnaireBuilderQuestionGroupProps) => {
    const { question, selected, selectedQuestion, onClick, onAdd, index, updateLayout } = props;
    const theme = useTheme();

    const renderNestedItem = useCallback(
      (item: any, idx: number) => (
        <QuestionnaireBuilderQuestionTitle
          key={item.ref}
          question={item}
          selected={selectedQuestion?.ref === item.ref}
          onClick={onClick}
          index={idx + index}
        />
      ),
      [selectedQuestion, onClick]
    );

    return (
      <Box
        width="100%"
        display="flex"
        flexDirection="column"
        borderRadius="4px"
        bgcolor={selected ? theme.palette.custom.secondaryBackground : "none"}
        border={`1px solid ${
          selected ? theme.palette.custom.hyperlink : theme.palette.custom.secondaryBorder
        }`}
        alignItems="center"
      >
        <Box
          width="100%"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          onClick={() => onClick(question)}
          sx={{
            ":hover": {
              cursor: "pointer",
            },
          }}
          borderBottom={
            (question.questions?.length ?? 0) > 0
              ? `1px solid ${theme.palette.custom.secondaryBorder}`
              : "none"
          }
          padding="8px"
        >
          <Box display="flex" gap="8px" alignItems="center">
            <Box
              display="flex"
              padding="4px"
              width="24px"
              height="24px"
              borderRadius="2px"
              bgcolor={
                selected ? theme.palette.custom.blueTypography : theme.palette.custom.primaryBorder
              }
              alignItems="center"
              justifyContent="center"
              gap="8px"
            >
              {question.icon && (
                <NavbarIcon
                  variant={question.icon as INavbarIconVariant}
                  color={
                    selected ? theme.palette.custom.white : theme.palette.custom.primaryTypography
                  }
                  sx={{
                    width: "16px",
                    height: "16px",
                    minHeight: "16px",
                    minWidth: "16px",
                    fill:
                      question.icon === "reports-list"
                        ? theme.palette.custom.primaryTypography
                        : undefined,
                  }}
                />
              )}
            </Box>
            <TableTextWithEllipsis
              value={question.title}
              variant="h4"
              color={
                selected ? theme.palette.custom.hyperlink : theme.palette.custom.primaryTypography
              }
              expanded
            />
          </Box>
          {onAdd && (
            <Box
              display="flex"
              width="22px"
              height="22px"
              minWidth="22px"
              bgcolor={theme.palette.custom.blueTypography}
              alignItems="center"
              justifyContent="center"
              borderRadius="50%"
              onClick={(e) => {
                e.stopPropagation();
                onAdd();
              }}
              sx={{
                ":hover": {
                  cursor: "pointer",
                },
              }}
            >
              <NavbarIcon
                sx={{
                  width: "12px",
                  height: "12px",
                  minHeight: "12px",
                  minWidth: "12px",
                }}
                variant="plus"
                color={theme.palette.custom.white}
              />
            </Box>
          )}
        </Box>
        {question.questions && (
          <Box width="100%" display="flex" flexDirection="column" padding="8px" gap="8px">
            {!updateLayout ? (
              question.questions?.map((groupQuestion, i) => (
                <QuestionnaireBuilderQuestionTitle
                  key={groupQuestion.ref}
                  question={groupQuestion}
                  selected={selectedQuestion?.ref === groupQuestion.ref}
                  onClick={onClick}
                  index={index + i}
                />
              ))
            ) : (
              <ListArrangement
                listItems={question.questions ?? []}
                updateLayout={updateLayout}
                renderItem={renderNestedItem}
                border={false}
              />
            )}
          </Box>
        )}
      </Box>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.question === nextProps.question &&
      prevProps.selected === nextProps.selected &&
      prevProps.selectedQuestion?.ref === nextProps.selectedQuestion?.ref &&
      prevProps.onClick === nextProps.onClick &&
      prevProps.updateLayout === nextProps.updateLayout
    );
  }
);
